import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Package, Star, Bell, Mail, TrendingUp, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA } from "@/lib/format";
import { formatDate } from "@/lib/format";
import { Loader } from "@/components/common";
import { ImageUploader } from "@/components/common";
import Modal, { TextField, TextArea, SelectField } from "@/components/Modal";
import AdminQuickStock from "@/components/AdminQuickStock";
import { PageTitle, Panel } from "./AdminShell";
import { toast } from "sonner";

const UNITS = ["unité", "mètre", "m²", "m³", "kilogramme", "tonne", "sac", "carton", "lot", "barre", "rouleau", "seau"];
const INSTALL_TYPES = [{ value: "none", label: "Non disponible (vente seule)" }, { value: "fixed", label: "Tarif prédéfini" }, { value: "quote", label: "Sur devis" }];

export default function AdminCatalog() {
  const [tab, setTab] = useState("products");
  return (
    <div>
      <PageTitle>Catalogue</PageTitle>
      <div className="flex gap-2 mb-4 flex-wrap">
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="tab-products">Produits</TabBtn>
        <TabBtn active={tab === "categories"} onClick={() => setTab("categories")} testid="tab-categories">Catégories</TabBtn>
        <TabBtn active={tab === "relances"} onClick={() => setTab("relances")} testid="tab-relances">Relances</TabBtn>
      </div>
      {tab === "products" && <Products />}
      {tab === "categories" && <Categories />}
      {tab === "relances" && <RelancesDashboard />}
    </div>
  );
}

function RestockDemands() {
  const [rows, setRows] = useState([]);
  const [contact, setContact] = useState(null);
  const load = useCallback(() => { api.get("/admin/stock-alerts").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  return (
    <Panel className="mb-4 p-4 border-l-4 border-amber-500" data-testid="restock-demands">
      <div className="flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-amber-600" /><h3 className="font-display font-bold text-[#0F172A]">Produits les plus demandés en rupture <span className="text-slate-400 font-normal">({rows.length})</span></h3></div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.product_id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0 gap-2" data-testid={`restock-demand-${r.product_id}`}>
            <span className="font-semibold text-slate-700 truncate">{r.name}</span>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-slate-400 whitespace-nowrap">Stock : {r.stock ?? "—"} {r.unit}</span>
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">{r.count} en attente</span>
              <button onClick={() => setContact(r)} className="inline-flex items-center gap-1 bg-[#0F172A] text-white text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-slate-800 transition whitespace-nowrap" data-testid={`contact-subscribers-${r.product_id}`}><Mail className="w-3.5 h-3.5" /> Contacter</button>
            </div>
          </div>
        ))}
      </div>
      {contact && <ContactSubscribersModal row={contact} onClose={() => setContact(null)} onDone={load} />}
    </Panel>
  );
}

function ContactSubscribersModal({ row, onClose, onDone }) {
  const [subject, setSubject] = useState(`${row.name} — bientôt de retour chez TOGOBATI`);
  const [message, setMessage] = useState(`Bonjour,\n\nVous vous êtes inscrit(e) pour être prévenu(e) au sujet du produit « ${row.name} ».\nNous tenons à vous informer de sa disponibilité prochaine. Restez connecté(e), il sera bientôt à nouveau commandable sur TOGOBATI.\n\nÀ très vite,\nL'équipe TOGOBATI`);
  const [scheduledAt, setScheduledAt] = useState("");
  const [subjectB, setSubjectB] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendId, setResendId] = useState(null);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const loadHistory = useCallback(() => {
    api.get(`/admin/stock-alerts/broadcasts?product_id=${row.product_id}`).then((r) => setHistory(r.data)).catch(() => {});
  }, [row.product_id]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { api.get("/admin/stock-alerts/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTemplate = (tid) => { const t = templates.find((x) => x.id === tid); if (t) { setSubject(t.subject); setMessage(t.message); toast.success(`Modèle « ${t.name} » chargé`); } };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du modèle ?");
    if (!name || !name.trim()) return;
    try { const { data } = await api.post("/admin/stock-alerts/templates", { name: name.trim(), subject, message }); setTemplates((s) => [data, ...s]); toast.success("Modèle enregistré"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return toast.error("Objet et message requis");
    setBusy(true);
    try {
      const payload = { subject, message };
      if (subjectB.trim()) payload.subject_b = subjectB.trim();
      if (scheduledAt) payload.scheduled_at = scheduledAt;
      const { data } = await api.post(`/admin/stock-alerts/${row.product_id}/contact`, payload);
      if (data.scheduled) toast.success("Envoi programmé avec succès");
      else toast.success(`Email envoyé à ${data.sent}/${data.recipients} abonné(s)`);
      setScheduledAt("");
      loadHistory();
      onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const resend = async (bid) => {
    setResendId(bid);
    try {
      const { data } = await api.post(`/admin/stock-alerts/broadcasts/${bid}/resend`);
      toast.success(`Renvoyé à ${data.sent}/${data.recipients} abonné(s)`);
      loadHistory(); onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setResendId(null); }
  };

  const cancelScheduled = async (bid) => {
    if (!window.confirm("Annuler cet envoi programmé ?")) return;
    try { await api.delete(`/admin/stock-alerts/broadcasts/${bid}`); toast.success("Envoi annulé"); loadHistory(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={`Contacter les abonnés — ${row.name}`} onClose={onClose} testid="contact-subscribers-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><strong className="text-amber-700">{row.count}</strong> client(s) en attente de ce produit recevront cet email.</p>
        {templates.length > 0 && (
          <select onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} defaultValue=""
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/50" data-testid="cs-template-select">
            <option value="" disabled>↧ Charger un modèle enregistré…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <TextField label="Objet" value={subject} onChange={setSubject} required testid="cs-subject" />
        <TextField label="Objet B — test A/B (facultatif)" value={subjectB} onChange={setSubjectB} required={false} testid="cs-subject-b" />
        {subjectB.trim() && <p className="text-[11px] text-slate-400 -mt-1">🅰️🅱️ Les abonnés seront répartis 50/50 entre les deux objets ; le gagnant (meilleur taux d'ouverture) apparaîtra dans l'historique.</p>}
        <TextArea label="Message" value={message} onChange={setMessage} required testid="cs-message" rows={7} />
        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Programmer l'envoi <span className="font-normal text-slate-400">(facultatif — laisser vide pour envoyer maintenant)</span></span>
          <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
            className="mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/50" data-testid="cs-scheduled-at" />
        </label>
        <div className="flex gap-2">
          <button disabled={busy} className="flex-1 bg-[#C85A32] text-white font-bold py-3 rounded-lg disabled:opacity-50" data-testid="cs-submit">{busy ? "Traitement…" : scheduledAt ? "Programmer l'envoi" : "Envoyer l'email groupé"}</button>
          <button type="button" onClick={saveAsTemplate} title="Enregistrer comme modèle" className="px-4 py-3 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold text-sm inline-flex items-center gap-1.5" data-testid="cs-save-template"><Save className="w-4 h-4" /> Modèle</button>
        </div>
      </form>
      {history.length > 0 && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="cs-history">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Historique des envois <span className="text-slate-400 font-normal">({history.length})</span></h4>
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {history.map((h) => {
              const scheduled = h.status === "scheduled";
              const base = h.sent || h.recipients || 0;
              const openRate = base ? Math.round((h.opened / base) * 100) : 0;
              return (
                <li key={h.id} className="text-sm border border-slate-100 rounded-lg px-3 py-2" data-testid={`cs-history-${h.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-700 truncate">{h.subject}</span>
                    {scheduled ? (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Programmé</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{h.sent}/{h.recipients} envoyé(s)</span>
                    )}
                  </div>
                  {scheduled ? (
                    <p className="text-xs text-blue-600 mt-1">📅 Programmé pour le {formatDate(h.scheduled_at)}{h.sent_by ? ` · par ${h.sent_by}` : ""}</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500" data-testid={`cs-stats-${h.id}`}>
                      <span>{formatDate(h.created_at)}{h.sent_by ? ` · par ${h.sent_by}` : ""}</span>
                      <span className="inline-flex items-center gap-1 text-slate-600">✉️ {h.delivered || 0} remis</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-[#C85A32]">👁️ {h.opened || 0} ouvert(s) ({openRate}%)</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-[#0F172A]">🔗 {h.clicked || 0} clic(s)</span>
                    </div>
                  )}
                  {h.variant_a && <ABResult b={h} />}
                  <div className="mt-1.5 flex gap-2">
                    {scheduled ? (
                      <button onClick={() => cancelScheduled(h.id)} className="text-xs font-bold text-red-600 hover:underline" data-testid={`cs-cancel-${h.id}`}>Annuler l'envoi</button>
                    ) : (
                      <button onClick={() => resend(h.id)} disabled={resendId === h.id} className="inline-flex items-center gap-1 text-xs font-bold text-[#0F172A] hover:underline disabled:opacity-50" data-testid={`cs-resend-${h.id}`}>
                        {resendId === h.id ? "Renvoi…" : "↻ Renvoyer"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function ABResult({ b }) {
  if (!b.variant_a) return null;
  const rate = (v) => ((v && v.sent) ? Math.round((v.opened / v.sent) * 100) : 0);
  const ra = rate(b.variant_a), rb = rate(b.variant_b);
  const winner = ra === rb ? null : (ra > rb ? "A" : "B");
  const cell = (k) => {
    const v = k === "A" ? b.variant_a : b.variant_b;
    const r = k === "A" ? ra : rb;
    const win = winner === k;
    return (
      <div key={k} className={`rounded-lg px-2 py-1.5 border ${win ? "border-[#C85A32] bg-[#FDF3EE]" : "border-slate-100"}`} data-testid={`ab-variant-${k}`}>
        <div className="flex items-center justify-between"><span className="font-bold text-slate-700">Objet {k}{win ? " 🏆" : ""}</span><span className="font-num font-bold text-[#C85A32]">{r}%</span></div>
        <p className="text-slate-500 truncate">{v?.subject}</p>
        <p className="text-slate-400">{v?.opened || 0}/{v?.sent || 0} ouvert(s) · {v?.clicked || 0} clic(s)</p>
      </div>
    );
  };
  return (
    <div className="mt-2" data-testid="ab-result">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Test A/B{winner ? ` — Objet ${winner} gagnant` : " — égalité"}</p>
      <div className="grid grid-cols-2 gap-2 text-xs">{cell("A")}{cell("B")}</div>
    </div>
  );
}

function RelancesDashboard() {
  const [rows, setRows] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const loadAll = useCallback(() => {
    api.get("/admin/stock-alerts/broadcasts").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/stock-alerts/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/stock-alerts/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (!rows) return <Loader />;

  const sentRows = rows.filter((r) => r.status !== "scheduled");
  const totalSent = sentRows.reduce((a, r) => a + (r.sent || 0), 0);
  const totalOpened = sentRows.reduce((a, r) => a + (r.opened || 0), 0);
  const totalDelivered = sentRows.reduce((a, r) => a + (r.delivered || 0), 0);
  const totalClicked = sentRows.reduce((a, r) => a + (r.clicked || 0), 0);
  const globalRate = totalSent ? Math.round((totalOpened / totalSent) * 100) : 0;
  const best = [...sentRows].filter((r) => (r.sent || 0) > 0).map((r) => ({ ...r, rate: Math.round(((r.opened || 0) / r.sent) * 100) })).sort((a, b) => b.rate - a.rate).slice(0, 3);

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="relances-kpis">
        <Kpi label="Relances envoyées" value={sentRows.length} />
        <Kpi label="Emails envoyés" value={totalSent} />
        <Kpi label="Ouvertures" value={totalOpened} />
        <Kpi label="Clics" value={totalClicked} />
        <Kpi label="Taux d'ouverture" value={`${globalRate}%`} accent />
      </div>

      {/* Meilleurs messages */}
      {best.length > 0 && (
        <Panel className="p-4" data-testid="relances-best">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-[#C85A32]" /><h3 className="font-display font-bold text-[#0F172A]">Meilleurs messages</h3></div>
          <div className="space-y-1.5">
            {best.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0 gap-2">
                <span className="font-semibold text-slate-700 truncate">{b.subject}</span>
                <span className="inline-flex items-center gap-1 bg-[#FDF3EE] text-[#C85A32] text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">{b.rate}% · {b.opened || 0}/{b.sent}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Relance automatique */}
      {cfg && <AutoRestockConfig cfg={cfg} onSaved={loadAll} />}

      {/* Modèles */}
      <TemplatesManager templates={templates} onChange={loadAll} />

      {/* Historique complet */}
      <Panel className="overflow-x-auto" data-testid="relances-table">
        <div className="p-4 pb-0"><h3 className="font-display font-bold text-[#0F172A]">Toutes les relances</h3></div>
        <table className="w-full text-sm mt-2">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Produit</th><th className="text-left px-4 py-3">Objet</th>
            <th className="text-center px-4 py-3">Déclencheur</th><th className="text-right px-4 py-3">Envoyés</th>
            <th className="text-right px-4 py-3">Ouverts</th><th className="text-right px-4 py-3">Clics</th><th className="text-left px-4 py-3">Date</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`relance-row-${r.id}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-700">{r.product_name || "—"}</td>
                <td className="px-4 py-2.5 text-slate-500 truncate max-w-[220px]">{r.subject}</td>
                <td className="px-4 py-2.5 text-center">
                  {r.status === "scheduled" ? <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Programmé</span>
                    : r.trigger === "auto" ? <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Auto</span>
                    : <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Manuel</span>}
                </td>
                <td className="px-4 py-2.5 text-right font-num">{r.status === "scheduled" ? "—" : `${r.sent || 0}/${r.recipients || 0}`}</td>
                <td className="px-4 py-2.5 text-right font-num text-[#C85A32] font-semibold">{r.status === "scheduled" ? "—" : (r.opened || 0)}</td>
                <td className="px-4 py-2.5 text-right font-num text-[#0F172A] font-semibold">{r.status === "scheduled" ? "—" : (r.clicked || 0)}</td>
                <td className="px-4 py-2.5 text-slate-400 text-xs">{formatDate(r.status === "scheduled" ? r.scheduled_at : r.created_at)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucune relance pour le moment.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <Panel className={`p-4 ${accent ? "border-l-4 border-[#C85A32]" : ""}`} data-testid={`kpi-${label}`}>
      <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`font-display font-black text-2xl mt-1 ${accent ? "text-[#C85A32]" : "text-[#0F172A]"} font-num`}>{value}</p>
    </Panel>
  );
}

function AutoRestockConfig({ cfg, onSaved }) {
  const [enabled, setEnabled] = useState(cfg.enabled !== false);
  const [subject, setSubject] = useState(cfg.subject || "");
  const [message, setMessage] = useState(cfg.message || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/stock-alerts/config", { enabled, subject, message }); toast.success("Relance automatique enregistrée"); onSaved?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-4" data-testid="auto-restock-config">
      <div className="flex items-center gap-2 mb-1"><Bell className="w-5 h-5 text-purple-600" /><h3 className="font-display font-bold text-[#0F172A]">Relance automatique — retour en stock</h3></div>
      <p className="text-xs text-slate-500 mb-3">Dès qu'un produit repasse en stock, cet email part automatiquement aux abonnés en attente. Utilisez <code className="bg-slate-100 px-1 rounded">{"{product}"}</code> pour insérer le nom du produit.</p>
      <label className="flex items-center gap-2 text-sm font-semibold mb-3"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="auto-restock-enabled" /> Activer la relance automatique</label>
      <div className="space-y-2">
        <TextField label="Objet" value={subject} onChange={setSubject} testid="auto-restock-subject" />
        <TextArea label="Message" value={message} onChange={setMessage} testid="auto-restock-message" rows={4} />
      </div>
      <button onClick={save} disabled={busy} className="mt-3 bg-[#0F172A] text-white font-bold text-sm px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="auto-restock-save"><Save className="w-4 h-4" /> {busy ? "Enregistrement…" : "Enregistrer"}</button>
    </Panel>
  );
}

function TemplatesManager({ templates, onChange }) {
  const [f, setF] = useState({ name: "", subject: "", message: "" });
  const [open, setOpen] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/stock-alerts/templates", f); toast.success("Modèle enregistré"); setF({ name: "", subject: "", message: "" }); setOpen(false); onChange?.(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (tid) => { if (!window.confirm("Supprimer ce modèle ?")) return; await api.delete(`/admin/stock-alerts/templates/${tid}`); toast.success("Supprimé"); onChange?.(); };
  return (
    <Panel className="p-4" data-testid="templates-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#C85A32]" /><h3 className="font-display font-bold text-[#0F172A]">Modèles d'email <span className="text-slate-400 font-normal">({templates.length})</span></h3></div>
        <button onClick={() => setOpen((o) => !o)} className="bg-[#C85A32] text-white font-bold text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="template-add-toggle"><Plus className="w-4 h-4" /> Nouveau</button>
      </div>
      {open && (
        <form onSubmit={add} className="space-y-2 mb-3 bg-slate-50 rounded-lg p-3" data-testid="template-form">
          <TextField label="Nom du modèle" value={f.name} onChange={(v) => setF((s) => ({ ...s, name: v }))} required testid="template-name" />
          <TextField label="Objet" value={f.subject} onChange={(v) => setF((s) => ({ ...s, subject: v }))} required testid="template-subject" />
          <TextArea label="Message" value={f.message} onChange={(v) => setF((s) => ({ ...s, message: v }))} required testid="template-message" rows={4} />
          <button className="bg-[#0F172A] text-white font-bold text-sm px-4 py-2 rounded-lg" data-testid="template-save">Enregistrer le modèle</button>
        </form>
      )}
      {templates.length ? (
        <div className="space-y-1.5">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0 gap-2" data-testid={`template-${t.id}`}>
              <div className="min-w-0"><span className="font-semibold text-slate-700">{t.name}</span><span className="text-slate-400 ml-2 truncate">— {t.subject}</span></div>
              <button onClick={() => del(t.id)} className="p-1.5 text-slate-400 hover:text-red-600 shrink-0" data-testid={`template-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-slate-400">Aucun modèle. Créez-en un pour le réutiliser en 1 clic.</p>}
    </Panel>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-lg text-sm font-semibold ${active ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{children}</button>;
}

function Products() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
    setProducts(p.data); setCategories(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm("Supprimer ce produit ?")) return; await api.delete(`/products/${id}`); toast.success("Supprimé"); load(); };
  const toggleFeat = async (p) => { try { await api.put(`/products/${p.id}/featured`, { featured: !p.featured }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!products) return <Loader />;
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <RestockDemands />
      <div className="flex justify-end mb-3">
        <button onClick={() => setEdit({})} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Nouveau produit</button>
      </div>
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Produit</th><th className="text-left px-4 py-3">Catégorie</th>
            <th className="text-right px-4 py-3">Prix</th><th className="text-right px-4 py-3">Stock</th>
            <th className="text-center px-4 py-3">Pose</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-product-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded bg-slate-100 overflow-hidden shrink-0">{(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2.5 text-slate-300" />}</div>
                    <div><p className="font-semibold text-slate-800 line-clamp-1">{p.name}</p><p className="text-xs text-slate-400 font-num">{p.reference}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-500">{catName(p.category_id)}</td>
                <td className="px-4 py-3 text-right font-num font-semibold">{formatFCFA(p.price)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className={`font-num ${p.stock <= p.alert_threshold ? "text-red-600 font-bold" : ""}`}>{p.stock}</span>
                    {p.stock <= p.alert_threshold && <AdminQuickStock product={p} onDone={load} compact />}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">{p.installation_type === "none" ? "—" : p.installation_type === "fixed" ? formatFCFA(p.installation_price) : "Devis"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => toggleFeat(p)} title="À la une" className={`p-1.5 ${p.featured ? "text-[#E08B00]" : "text-slate-300 hover:text-[#E08B00]"}`} data-testid={`feat-product-${p.id}`}><Star className={`w-4 h-4 ${p.featured ? "fill-[#E08B00]" : ""}`} /></button>
                  <button onClick={() => setEdit(p)} className="p-1.5 text-slate-500 hover:text-[#C85A32]" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`del-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      {edit && <ProductForm product={edit} categories={categories} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function ProductForm({ product, categories, onClose, onDone }) {
  const [f, setF] = useState({
    name: "", reference: "", category_id: "", subcategory: "", description: "", brand: "", specs: "",
    unit: "unité", price: 0, promo_price: 0, promo_end: "", stock: 0, alert_threshold: 10, available: true,
    installation_type: "none", installation_price: 0, images: [], is_pack: false, pack_contents: [], featured: false, ...product,
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const cat = categories.find((c) => c.id === f.category_id);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, price: Number(f.price), promo_price: Number(f.promo_price), stock: Number(f.stock), alert_threshold: Number(f.alert_threshold), installation_price: Number(f.installation_price), is_pack: !!f.is_pack, pack_contents: f.pack_contents || [] };
    try {
      if (product.id) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Enregistré"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={product.id ? "Modifier le produit" : "Nouveau produit"} onClose={onClose} testid="product-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom" value={f.name} onChange={set("name")} required testid="pf-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <SelectField label="Catégorie" value={f.category_id} onChange={set("category_id")} testid="pf-category" options={categories.map((c) => ({ value: c.id, label: c.name }))} />
          <SelectField label="Sous-catégorie" value={f.subcategory} onChange={set("subcategory")} testid="pf-subcategory" options={(cat?.subcategories || []).map((s) => ({ value: s, label: s }))} />
        </div>
        <TextArea label="Description" value={f.description} onChange={set("description")} testid="pf-desc" required={false} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Marque" value={f.brand} onChange={set("brand")} testid="pf-brand" required={false} />
          <SelectField label="Unité de vente" value={f.unit} onChange={set("unit")} testid="pf-unit" options={UNITS.map((u) => ({ value: u, label: u }))} />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField label="Prix (FCFA)" type="number" value={f.price} onChange={set("price")} testid="pf-price" />
          <TextField label="Stock" type="number" value={f.stock} onChange={set("stock")} testid="pf-stock" />
          <TextField label="Seuil d'alerte" type="number" value={f.alert_threshold} onChange={set("alert_threshold")} testid="pf-threshold" required={false} />
        </div>
        <TextArea label="Caractéristiques techniques" value={f.specs} onChange={set("specs")} testid="pf-specs" required={false} rows={2} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Prix promo (FCFA — 0 = aucune)" type="number" value={f.promo_price} onChange={set("promo_price")} testid="pf-promo" required={false} />
          <TextField label="Fin de la promotion" type="date" value={f.promo_end} onChange={set("promo_end")} testid="pf-promo-end" required={false} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SelectField label="Installation" value={f.installation_type} onChange={set("installation_type")} testid="pf-install-type" options={INSTALL_TYPES} placeholder={null} />
          {f.installation_type === "fixed" && <TextField label="Prix installation (FCFA)" type="number" value={f.installation_price} onChange={set("installation_price")} testid="pf-install-price" />}
        </div>
        <div><span className="text-sm font-semibold text-slate-700 block mb-1">Photos <span className="font-normal text-slate-400">— ajoutez plusieurs photos ; la 1ère sert de couverture (glissez-déposez pour réordonner)</span></span><ImageUploader value={f.images} onChange={set("images")} testid="pf-images" /></div>
        <div className="bg-[#FDF3EE] border border-[#C85A32]/20 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.is_pack} onChange={(e) => set("is_pack")(e.target.checked)} data-testid="pf-is-pack" /> C'est un pack (articles + pose, prix tout compris)</label>
          {f.is_pack && (
            <div className="mt-2">
              <span className="text-xs text-slate-500">Contenu du pack (un élément par ligne)</span>
              <textarea value={(f.pack_contents || []).join("\n")} onChange={(e) => set("pack_contents")(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} rows={3} className="mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={"1× Climatiseur 1.5CV\n1× Kit de pose\nMain d'œuvre incluse"} data-testid="pf-pack-contents" />
              <p className="text-[11px] text-slate-400 mt-1">Le prix ci-dessus correspond au prix total du pack (articles + installation).</p>
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.available} onChange={(e) => set("available")(e.target.checked)} data-testid="pf-available" /> Produit disponible à la vente</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured")(e.target.checked)} data-testid="pf-featured" /> Mettre « À la une » (mis en avant sur l'accueil)</label>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="pf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

function Categories() {
  const [cats, setCats] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/categories/${id}`); load(); };
  if (!cats) return <Loader />;
  return (
    <div>
      <div className="flex justify-end mb-3"><button onClick={() => setEdit({})} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-category-btn"><Plus className="w-4 h-4" /> Nouvelle catégorie</button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map((c) => (
          <Panel key={c.id} className="p-4" data-testid={`admin-category-${c.id}`}>
            <div className="flex justify-between items-start">
              <div><p className="font-display font-bold text-lg">{c.name}</p><p className="text-xs text-slate-400">{c.description}</p></div>
              <div className="flex"><button onClick={() => setEdit(c)} className="p-1.5 text-slate-500"><Pencil className="w-4 h-4" /></button><button onClick={() => del(c.id)} className="p-1.5 text-red-500"><Trash2 className="w-4 h-4" /></button></div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">{(c.subcategories || []).map((s) => <span key={s} className="text-[11px] bg-slate-100 px-2 py-0.5 rounded-full">{s}</span>)}</div>
          </Panel>
        ))}
      </div>
      {edit && <CategoryForm cat={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function CategoryForm({ cat, onClose, onDone }) {
  const [f, setF] = useState({ name: "", slug: "", icon: "Package", icon_image: "", description: "", subcategories: [], ...cat });
  const [subInput, setSubInput] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, slug: f.slug || f.name.toLowerCase().replace(/\s+/g, "-") };
    try {
      if (cat.id) await api.put(`/categories/${cat.id}`, payload); else await api.post("/categories", payload);
      toast.success("Enregistré"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={cat.id ? "Modifier la catégorie" : "Nouvelle catégorie"} onClose={onClose} testid="category-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom" value={f.name} onChange={set("name")} required testid="cf-name" />
        <TextField label="Description" value={f.description} onChange={set("description")} testid="cf-desc" required={false} />
        <TextField label="Icône (lucide)" value={f.icon} onChange={set("icon")} testid="cf-icon" required={false} placeholder="Package, Wrench, Zap..." />
        <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Icône image (facultatif)</span>
          <ImageUploader value={f.icon_image ? [f.icon_image] : []} onChange={(arr) => set("icon_image")(arr[0] || "")} multiple={false} label="Téléverser une icône" testid="cf-icon-image" />
        </div>
        <div>
          <span className="text-sm font-semibold text-slate-700">Sous-catégories</span>
          <div className="flex gap-2 mt-1">
            <input value={subInput} onChange={(e) => setSubInput(e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Ajouter..." data-testid="cf-sub-input" />
            <button type="button" onClick={() => { if (subInput) { set("subcategories")([...(f.subcategories || []), subInput]); setSubInput(""); } }} className="bg-slate-200 px-3 rounded-lg text-sm font-semibold">Ajouter</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">{(f.subcategories || []).map((s, i) => <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded-full">{s} <button type="button" onClick={() => set("subcategories")(f.subcategories.filter((_, idx) => idx !== i))}>×</button></span>)}</div>
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="cf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
